import type mapboxgl from "mapbox-gl";
import { useEffect, useRef } from "react";

type Options = {
  enabled: boolean;
  decay?: number; // momentum decay factor
  wheelZoomMultiplier?: number;
  scrollStopTimeout?: number; // timeout in ms to detect when scrolling stops
  disableOnTouch?: boolean; // automatically disable on touch devices
};

// Utility function to detect touch devices
const isTouchDevice = (): boolean => {
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0
  );
};

export function useCenterScrollZoom(
  map: mapboxgl.Map | undefined,
  options: Options,
) {
  const {
    enabled,
    decay = 0.6,
    wheelZoomMultiplier = 0.01,
    scrollStopTimeout = 150,
    disableOnTouch = true,
  } = options;

  // Automatically disable on touch devices if disableOnTouch is true
  const shouldEnable = enabled && !(disableOnTouch && isTouchDevice());

  const zoomDeltaRef = useRef(0);
  const isZoomingRef = useRef(false);
  const touchStartDistanceRef = useRef<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // New refs for detecting when scrolling/touching stops
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const touchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTouchMoveTimeRef = useRef<number>(0);
  const isTouchActiveRef = useRef(false);

  useEffect(() => {
    if (!map) return;

    // Clean up previous controller if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!shouldEnable) {
      map.scrollZoom.enable();
      return;
    }

    // Create new abort controller for this effect
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    map.scrollZoom.disable(); // disable default scroll behavior

    const canvas = map.getCanvas();

    const stopMomentum = () => {
      zoomDeltaRef.current = 0;
      isZoomingRef.current = false;
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = -e.deltaY * wheelZoomMultiplier;
      zoomDeltaRef.current += delta;

      // Clear any existing timeout and set a new one
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        // Stop momentum if no wheel events for the timeout period
        stopMomentum();
      }, scrollStopTimeout);

      if (!isZoomingRef.current) {
        isZoomingRef.current = true;
        requestAnimationFrame(animateZoom);
      }
    };

    const animateZoom = () => {
      // Check if controller was aborted
      if (abortController.signal.aborted) {
        stopMomentum();
        return;
      }

      const delta = zoomDeltaRef.current;

      if (Math.abs(delta) < 0.0001) {
        stopMomentum();
        return;
      }

      const newZoom = map.getZoom() + delta;

      map.easeTo({
        zoom: newZoom,
        center: map.getCenter(),
        duration: 35,
      });

      zoomDeltaRef.current *= decay;
      requestAnimationFrame(animateZoom);
    };

    const getTouchDistance = (touches: TouchList) => {
      const [a, b] = [touches[0], touches[1]];
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        touchStartDistanceRef.current = getTouchDistance(e.touches);
        isTouchActiveRef.current = true;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && touchStartDistanceRef.current !== null) {
        e.preventDefault();
        const newDistance = getTouchDistance(e.touches);
        const delta = (newDistance - touchStartDistanceRef.current) * 0.005;
        touchStartDistanceRef.current = newDistance;
        lastTouchMoveTimeRef.current = Date.now();

        // Clear any existing touch timeout
        if (touchTimeoutRef.current) {
          clearTimeout(touchTimeoutRef.current);
        }

        // Set timeout to detect when touch movement stops
        touchTimeoutRef.current = setTimeout(() => {
          // If touches are still active but no movement, we should stop momentum-like behavior
          if (isTouchActiveRef.current) {
            // For touch, we don't use momentum like wheel scroll, so this is mainly for consistency
          }
        }, scrollStopTimeout);

        map.easeTo({
          zoom: map.getZoom() + delta,
          center: map.getCenter(),
          duration: 50,
        });
      }
    };

    const onTouchEnd = () => {
      touchStartDistanceRef.current = null;
      isTouchActiveRef.current = false;

      // Clear touch timeout when touches end
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }
    };

    // Add event listeners with abort signal
    canvas.addEventListener("wheel", onWheel, {
      passive: false,
      signal: abortController.signal,
    });
    canvas.addEventListener("touchstart", onTouchStart, {
      passive: false,
      signal: abortController.signal,
    });
    canvas.addEventListener("touchmove", onTouchMove, {
      passive: false,
      signal: abortController.signal,
    });
    canvas.addEventListener("touchend", onTouchEnd, {
      signal: abortController.signal,
    });
    canvas.addEventListener("touchcancel", onTouchEnd, {
      signal: abortController.signal,
    });

    return () => {
      // Abort all listeners and clean up
      abortController.abort();

      // Clear all timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
        touchTimeoutRef.current = null;
      }

      // Reset all refs
      zoomDeltaRef.current = 0;
      isZoomingRef.current = false;
      touchStartDistanceRef.current = null;
      isTouchActiveRef.current = false;

      // Reset scroll zoom
      map?.scrollZoom.enable();
    };
  }, [map, shouldEnable, decay, wheelZoomMultiplier, scrollStopTimeout]);
}
